import React, { useState } from 'react';
import { Button, AvgChip, FamilyIcon, TwistButton, Panel, BatchStatus, ProgressBar, useToast } from '../../components/ui';
import { typeOfExamType } from '../../config/assessmentFamilies';
import { resultsForExamSet, statsFor } from './derive';
import ResultRow, { byScore } from './ResultRow';
import { batchStatusOf, batchWindow } from './batches';
import { sendSingleInvite } from './actions';

function RowsHead({ withBatch }) {
  return (
    <div className={`wz-rrow ${withBatch ? 'wb' : 'nb'} th`}>
      <span>Candidate</span>{withBatch && <span>Batch</span>}<span>Status</span><span>Score</span><span>Date</span><span>Time</span><span>Stage</span><span />
    </div>
  );
}

/**
 * Prototype viewByInterview(): one collapsible section per interview with its
 * results, sorted by score. The "All interviews" filter narrows the sections;
 * search and stage apply to the rows. Collapsing never resets filters.
 */
export function ByInterviewView({ data, hireBar, ctx, collapsed, onToggle, onSplitByBatch }) {
  const withBatch = true;
  if (ctx.ivFilter === 'unassigned') {
    const ppl = data.people.filter((p) => p.unassigned.length && ctx.match(p));
    return (
      <Panel className="wz-sec" style={{ overflowX: 'auto' }}>
        <div className="wz-sec-head"><div className="grow"><b style={{ fontSize: 15 }}>Unassigned</b><span className="wz-sub">On this position, not in any interview</span></div></div>
        {ppl.length ? (
          <div style={{ minWidth: 900 }}>
            <RowsHead withBatch={false} />
            {ppl.map((p) => <ResultRow key={p.key} p={p} r={p.unassigned[0]} examType={null} withBatch={false} act={ctx.act} busyId={ctx.busyId} />)}
          </div>
        ) : <p className="wz-sub" style={{ padding: 20 }}>No candidates match these filters.</p>}
      </Panel>
    );
  }
  const ivs = data.examSets.filter((es) => ctx.ivFilter === 'all' || es.id === ctx.ivFilter);
  if (!ivs.length) return <Panel className="wz-empty"><p>No interviews yet.</p></Panel>;
  return ivs.map((es) => {
    const key = `iv:${es.id}`;
    const open = !collapsed.has(key);
    const t = typeOfExamType(es.exam_type);
    const s = statsFor(resultsForExamSet(data.people, es.id), hireBar);
    const nb = (data.batchesByExamSet[es.id] || []).length;
    const ppl = data.people.filter((p) => p.results[es.id] && ctx.match(p)).sort(byScore(es.id));
    return (
      <Panel className="wz-sec" key={es.id} style={{ overflowX: 'auto' }}>
        <div className="wz-sec-head">
          <TwistButton expanded={open} label={`${open ? 'Collapse' : 'Expand'} ${es.name}`} onClick={() => onToggle(key)} />
          <div className="grow">
            <FamilyIcon fam={t.fam} />
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <b style={{ fontSize: 15 }}>{es.name}</b>
              <span className="wz-sub">{t.name}{withBatch ? `, ${nb} batch${nb === 1 ? '' : 'es'}` : ''}</span>
            </span>
          </div>
          <div className="wz-sec-stats">
            <span><b>{s.total}</b> assigned</span>
            <span><b>{s.done}</b> completed</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Avg <AvgChip value={s.avg} hireBar={s.hireBar} /></span>
          </div>
          {withBatch && <Button small onClick={() => onSplitByBatch(es.id)}>Split by batch</Button>}
        </div>
        {open && (ppl.length ? (
          <div>{/* columns carry their own minimums (position.css .wz-rrow) */}
            <RowsHead withBatch={withBatch} />
            {ppl.map((p) => (
              <ResultRow key={p.key} p={p} r={p.results[es.id]} examType={es.exam_type} withBatch={withBatch} batchesById={data.batchesById} act={ctx.act} busyId={ctx.busyId} />
            ))}
          </div>
        ) : <p className="wz-sub" style={{ padding: 20 }}>{s.total ? 'No candidates match these filters.' : 'No candidates assigned to this interview yet.'}</p>)}
      </Panel>
    );
  });
}

/**
 * Prototype viewByBatch(): pick an interview, then one collapsible section per
 * batch (status, window, completed of total with bar, above-bar count, average,
 * Add, "Remind N"). Rows without a batch get a "No batch" section.
 */
export function ByBatchView({ data, hireBar, ctx, collapsed, onToggle, batchIv, setBatchIv, onAdd, onNewBatch }) {
  const toast = useToast();
  const [reminding, setReminding] = useState(null);
  /** "Remind N": today's single invite/resend for every not-started row of the batch. */
  const onRemind = async (key, rows) => {
    setReminding(key);
    let ok = 0;
    for (const row of rows) {
      try { await sendSingleInvite(row.id); ok += 1; } catch { /* counted below */ } // eslint-disable-line no-await-in-loop
    }
    setReminding(null);
    toast(ok === rows.length ? `Reminders sent to ${ok} candidate${ok === 1 ? '' : 's'}` : `Reminders sent to ${ok} of ${rows.length}`);
    data.reloadAssignments();
  };
  const iv = data.examSets.find((es) => es.id === batchIv) || data.examSets[0];
  if (!iv) return <Panel className="wz-empty"><p>No interviews yet.</p></Panel>;
  const picker = (
    <div className="wz-ivpick" role="group" aria-label="Interview">
      {data.examSets.map((x) => {
        const n = (data.batchesByExamSet[x.id] || []).length;
        return (
          <button key={x.id} type="button" aria-pressed={x.id === iv.id} onClick={() => setBatchIv(x.id)}>
            <FamilyIcon fam={typeOfExamType(x.exam_type).fam} size="m" />
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <b style={{ fontSize: 13 }}>{x.name}</b>
              <span className="wz-sub" style={{ fontSize: 12 }}>{n} batch{n === 1 ? '' : 'es'}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
  const list = data.batchesByExamSet[iv.id] || [];
  const results = resultsForExamSet(data.people, iv.id);
  const known = new Set(list.map((b) => b.id));
  const orphan = results.some((r) => !r.batchId || !known.has(r.batchId));
  if (!list.length && !orphan) {
    return (
      <>
        {picker}
        {data.batchesError && <div className="wz-error" role="alert" style={{ marginBottom: 16 }}>{data.batchesError}</div>}
        <Panel className="wz-empty">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{iv.name} has no batches yet</h3>
          <p>Create a batch to invite candidates and track them here.</p>
          <Button variant="primary" icon="plus" onClick={() => onNewBatch(iv.id)}>New batch</Button>
        </Panel>
      </>
    );
  }
  const sections = [
    ...list.map((b) => ({ b, key: `${iv.id}:${b.id}`, filter: (r) => r.batchId === b.id })),
    ...(orphan ? [{ b: null, key: `${iv.id}:none`, filter: (r) => !r.batchId || !known.has(r.batchId) }] : []),
  ];
  return (
    <>
      {picker}
      {sections.map(({ b, key, filter }) => {
        const open = !collapsed.has(key);
        const s = statsFor(results, hireBar, filter);
        const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
        const ppl = data.people.filter((p) => p.results[iv.id] && filter(p.results[iv.id]) && ctx.match(p)).sort(byScore(iv.id));
        const name = b ? b.name : 'No batch';
        const notStartedRows = ppl.map((p) => p.results[iv.id]).filter((r) => r.state === 'invited' && !String(r.row.id).startsWith('synced-'));
        const status = b ? batchStatusOf(b) : null;
        return (
          <Panel className="wz-sec" key={key} style={{ overflowX: 'auto' }}>
            <div className="wz-sec-head">
              <TwistButton expanded={open} label={`${open ? 'Collapse' : 'Expand'} ${name}`} onClick={() => onToggle(key)} />
              <div className="grow">
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}><b style={{ fontSize: 15 }}>{name}</b>{b && <BatchStatus status={status} />}</span>
                  <span className="wz-sub">{b ? batchWindow(b) : 'Results from before batches existed'}</span>
                </span>
              </div>
              <div className="wz-sec-stats">
                <span style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 150 }}>
                  <span><b>{s.done}</b> of <b>{s.total}</b> completed</span>
                  <ProgressBar pct={pct} />
                </span>
                <span><b>{s.above}</b> above bar</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Avg <AvgChip value={s.avg} hireBar={s.hireBar} /></span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {b && <Button small icon="userAdd" disabled={status === 'closed'} title={status === 'closed' ? 'This batch is closed' : undefined} onClick={() => onAdd(iv.id, b.id)}>Add</Button>}
                {notStartedRows.length > 0 && (
                  <Button small disabled={reminding === key} onClick={() => onRemind(key, notStartedRows.map((r) => r.row))}>{reminding === key ? 'Sending…' : `Remind ${notStartedRows.length}`}</Button>
                )}
              </div>
            </div>
            {open && (ppl.length ? (
              <div style={{ minWidth: 900 }}>
                <RowsHead withBatch={false} />
                {ppl.map((p) => (
                  <ResultRow key={p.key} p={p} r={p.results[iv.id]} examType={iv.exam_type} withBatch={false} act={ctx.act} busyId={ctx.busyId} />
                ))}
              </div>
            ) : <p className="wz-sub" style={{ padding: 20 }}>{s.total ? 'No candidates match these filters.' : 'No candidates in this batch yet.'}</p>)}
          </Panel>
        );
      })}
    </>
  );
}
